import { useEffect, useState } from "react";
import { Briefcase, Edit3, Info, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input } from "@/components/ui/Inputs";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { useMe } from "@/hooks/useMe";
import {
  useCreateTeamProfile,
  useDeleteTeamProfile,
  useTeamProfiles,
  useUpdateTeamProfile,
} from "@/hooks/useTeamProfiles";
import type { TeamProfile, TeamProfileBody } from "@/lib/api";

/**
 * Settings → Team Profiles
 *
 * Org-level cast list. Admins curate it; everyone reads it. Each profile
 * is the source-of-truth canonical record; the AI tailors it per RFP
 * inside `kind=personnel` sections.
 *
 * Layout: list on the left, editor card on the right when you click a
 * profile or hit "Add". The editor lets you set the structured fields
 * and the long-form canonical CV — that's the input the AI actually
 * works from, so it's worth giving it real estate.
 */
export default function SettingsTeamProfiles() {
  const me = useMe();
  const profilesQ = useTeamProfiles();
  const isAdmin = me.data?.user.role === "admin";

  const [editing, setEditing] = useState<TeamProfile | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TeamProfile | null>(null);

  const profiles: TeamProfile[] = profilesQ.data?.profiles ?? [];

  if (me.isLoading || profilesQ.isLoading) {
    return (
      <Layout>
        <PageHeader title="Team Profiles" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title="Team Profiles"
        subtitle={
          isAdmin
            ? "Curate your agency's roster. The AI tailors these per RFP inside Personnel sections."
            : "The agency's roster. Admins maintain it; bid managers feature people in Personnel sections."
        }
        actions={
          isAdmin && !creating && !editing ? (
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={14} />}
              onClick={() => setCreating(true)}
            >
              Add profile
            </Button>
          ) : null
        }
      />

      <div className="p-8 pb-24 max-w-5xl">
        <Card className="mb-4">
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
            >
              <Info size={16} style={{ color: "#F97316" }} />
            </div>
            <div className="text-xs" style={{ color: "#9CA3AF" }}>
              <p className="text-white text-sm font-medium mb-1">How tailoring works</p>
              <p>
                Write each person's canonical CV once, in long form. When a bid manager opens a
                Personnel section, they pick which people to feature; the AI extracts what's
                relevant to that RFP and stitches the profiles together. Tweak the result in the
                section editor.
              </p>
            </div>
          </div>
        </Card>

        {creating && isAdmin && <ProfileEditor profile={null} onClose={() => setCreating(false)} />}

        {editing && isAdmin && (
          <ProfileEditor
            profile={editing}
            onClose={() => setEditing(null)}
            onDelete={() => setPendingDelete(editing)}
          />
        )}

        {!creating && !editing && profiles.length === 0 && (
          <Card>
            <div className="text-center py-8">
              <Briefcase size={28} className="mx-auto mb-2" style={{ color: "#6B7280" }} />
              <p className="text-sm text-white font-medium">No team profiles yet</p>
              <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
                {isAdmin
                  ? "Add your first profile to start tailoring CVs per RFP."
                  : "Ask your admin to add the team's profiles."}
              </p>
            </div>
          </Card>
        )}

        {!creating && !editing && profiles.length > 0 && (
          <div className="space-y-2">
            {profiles.map((p) => (
              <ProfileRow
                key={p.id}
                profile={p}
                canEdit={isAdmin}
                onEdit={() => setEditing(p)}
                onDelete={() => setPendingDelete(p)}
              />
            ))}
          </div>
        )}
      </div>

      <DeleteProfileDialog
        target={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={() => {
          setPendingDelete(null);
          if (editing && pendingDelete?.id === editing.id) setEditing(null);
        }}
      />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function ProfileRow({
  profile,
  canEdit,
  onEdit,
  onDelete,
}: {
  profile: TeamProfile;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="rounded-xl border px-4 py-3 flex items-center gap-3"
      style={{
        backgroundColor: "#161B22",
        borderColor: "rgba(229,231,235,0.08)",
      }}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{profile.fullName}</p>
        <p className="text-xs mt-0.5 truncate" style={{ color: "#9CA3AF" }}>
          {[profile.role, profile.yearsExperience ? `${profile.yearsExperience}y experience` : null]
            .filter(Boolean)
            .join(" · ") || "No role set"}
        </p>
        {profile.expertiseTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {profile.expertiseTags.slice(0, 6).map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{
                  color: "#9CA3AF",
                  backgroundColor: "rgba(255,255,255,0.04)",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
            title="Edit profile"
          >
            <Edit3 size={13} />
          </button>
          <button
            onClick={onDelete}
            className="text-gray-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10"
            title="Delete profile"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor — used for both create and update.
// ---------------------------------------------------------------------------

function ProfileEditor({
  profile,
  onClose,
  onDelete,
}: {
  profile: TeamProfile | null;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const create = useCreateTeamProfile();
  const update = useUpdateTeamProfile();

  const [body, setBody] = useState<TeamProfileBody>(() => ({
    fullName: profile?.fullName ?? "",
    role: profile?.role ?? "",
    yearsExperience: profile?.yearsExperience ?? null,
    bio: profile?.bio ?? "",
    expertiseTags: profile?.expertiseTags ?? [],
    canonicalCvText: profile?.canonicalCvText ?? "",
    certifications: profile?.certifications ?? [],
    linkedinUrl: profile?.linkedinUrl ?? null,
  }));

  const [tagInput, setTagInput] = useState("");
  const [certInput, setCertInput] = useState("");

  // Reset when switching profiles.
  useEffect(() => {
    setBody({
      fullName: profile?.fullName ?? "",
      role: profile?.role ?? "",
      yearsExperience: profile?.yearsExperience ?? null,
      bio: profile?.bio ?? "",
      expertiseTags: profile?.expertiseTags ?? [],
      canonicalCvText: profile?.canonicalCvText ?? "",
      certifications: profile?.certifications ?? [],
      linkedinUrl: profile?.linkedinUrl ?? null,
    });
  }, [profile?.id]);

  const onSave = async () => {
    if (!body.fullName.trim()) {
      toast.error("Full name is required.");
      return;
    }
    try {
      if (profile) {
        await update.mutateAsync({ profileId: profile.id, patch: body });
        toast.success("Profile updated.");
      } else {
        await create.mutateAsync(body);
        toast.success("Profile added.");
      }
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">
            {profile ? `Edit · ${profile.fullName}` : "Add team profile"}
          </h3>
          <div className="flex items-center gap-2">
            {profile && onDelete && (
              <button
                onClick={onDelete}
                className="text-xs inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 px-2.5 py-1.5 rounded-md hover:bg-red-500/10"
              >
                <Trash2 size={12} />
                Delete
              </button>
            )}
            <Button variant="ghost" size="sm" icon={<X size={14} />} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Save size={14} />}
              onClick={onSave}
              loading={busy}
            >
              {profile ? "Save changes" : "Add profile"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Full name" required>
            <Input
              value={body.fullName}
              onChange={(e) => setBody((b) => ({ ...b, fullName: e.target.value }))}
              placeholder="e.g. Jane Doe"
            />
          </Field>
          <Field label="Role / title">
            <Input
              value={body.role ?? ""}
              onChange={(e) => setBody((b) => ({ ...b, role: e.target.value }))}
              placeholder="e.g. Principal Strategist"
            />
          </Field>
          <Field label="Years of experience">
            <Input
              type="number"
              min={0}
              max={80}
              value={body.yearsExperience ?? ""}
              onChange={(e) =>
                setBody((b) => ({
                  ...b,
                  yearsExperience: e.target.value === "" ? null : Number(e.target.value),
                }))
              }
              placeholder="Optional"
            />
          </Field>
          <Field label="LinkedIn URL">
            <Input
              value={body.linkedinUrl ?? ""}
              onChange={(e) =>
                setBody((b) => ({
                  ...b,
                  linkedinUrl: e.target.value === "" ? null : e.target.value,
                }))
              }
              placeholder="Optional"
            />
          </Field>
        </div>

        <Field
          label="Short bio"
          hint="1–3 sentences. Shown as a quick-glance summary in the picker."
        >
          <textarea
            value={body.bio ?? ""}
            onChange={(e) => setBody((b) => ({ ...b, bio: e.target.value }))}
            placeholder="Strategic thinker with deep tourism-sector experience…"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30 resize-y"
            style={{ minHeight: 70 }}
          />
        </Field>

        <Field
          label="Expertise tags"
          hint="Free-form labels you filter on when picking — e.g. strategy, creative, media planning."
        >
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(body.expertiseTags ?? []).map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-white/5 border border-white/10"
              >
                <span className="text-white">{t}</span>
                <button
                  onClick={() =>
                    setBody((b) => ({
                      ...b,
                      expertiseTags: (b.expertiseTags ?? []).filter((x) => x !== t),
                    }))
                  }
                  className="text-gray-400 hover:text-white"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tagInput.trim()) {
                  e.preventDefault();
                  setBody((b) => ({
                    ...b,
                    expertiseTags: Array.from(
                      new Set([...(b.expertiseTags ?? []), tagInput.trim()]),
                    ),
                  }));
                  setTagInput("");
                }
              }}
              placeholder="Type a tag and press Enter"
            />
          </div>
        </Field>

        <Field
          label="Certifications"
          hint="One per line. Surfaced as a bullet in tailored output when the RFP type warrants."
        >
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(body.certifications ?? []).map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-white/5 border border-white/10"
              >
                <span className="text-white">{c}</span>
                <button
                  onClick={() =>
                    setBody((b) => ({
                      ...b,
                      certifications: (b.certifications ?? []).filter((x) => x !== c),
                    }))
                  }
                  className="text-gray-400 hover:text-white"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
          <Input
            value={certInput}
            onChange={(e) => setCertInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && certInput.trim()) {
                e.preventDefault();
                setBody((b) => ({
                  ...b,
                  certifications: Array.from(
                    new Set([...(b.certifications ?? []), certInput.trim()]),
                  ),
                }));
                setCertInput("");
              }
            }}
            placeholder="e.g. PMP (2020) — press Enter"
          />
        </Field>

        <Field
          label="Canonical CV"
          hint="The long-form source of truth. Every credential, role, and project the agency wants to draw from. The AI extracts and reorganises from this — never invents."
        >
          <textarea
            value={body.canonicalCvText ?? ""}
            onChange={(e) => setBody((b) => ({ ...b, canonicalCvText: e.target.value }))}
            placeholder="Paste a complete CV. Markdown-ish — bullets, paragraphs."
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30 resize-y font-mono"
            style={{ minHeight: 240 }}
          />
        </Field>
      </div>
    </Card>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p
        className="text-[10px] uppercase tracking-wider font-bold mb-1.5"
        style={{ color: "#9CA3AF" }}
      >
        {label}
        {required && <span style={{ color: "#F97316" }}> *</span>}
      </p>
      {children}
      {hint && (
        <p className="text-[11px] mt-1" style={{ color: "#6B7280" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function DeleteProfileDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: TeamProfile | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const remove = useDeleteTeamProfile();
  return (
    <ConfirmDeleteDialog
      open={!!target}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={target ? `Delete ${target.fullName}?` : ""}
      description="The profile will be removed from the org. Sections that reference it will need a new pick."
      confirmLabel="Delete"
      onConfirm={async () => {
        if (!target) return;
        try {
          await remove.mutateAsync(target.id);
          toast.success("Profile deleted.");
          onDeleted();
        } catch (err) {
          toast.error((err as Error).message);
        }
      }}
      busy={remove.isPending}
    />
  );
}
